import { useCallback, useEffect, useMemo } from "react";
import type { Route } from "react-router";
import { push } from "react-router-redux";
import { t } from "ttag";
import _ from "underscore";

import { CollectionPermissionsHelp } from "metabase/admin/permissions/components/CollectionPermissionsHelp";
import { useListCollectionsTreeQuery } from "metabase/api";
import { connect, useDispatch, useSelector } from "metabase/redux";
import type { State } from "metabase/redux/store";
import type { Collection, CollectionId } from "metabase-types/api";

import { DashboardPermissionsSection } from "../../components/DashboardPermissionsSection";
import {
  PermissionsEditor,
  PermissionsEditorEmptyState,
} from "../../components/PermissionsEditor";
import { PermissionsPageLayout } from "../../components/PermissionsPageLayout";
import { PermissionsSidebar } from "../../components/PermissionsSidebar";
import {
  type UpdateCollectionPermissionParams,
  initializeCollectionPermissions,
  initializeDashboardPermissions,
  loadCollectionPermissions,
  loadDashboardPermissions,
  saveCollectionPermissions,
  saveDashboardPermissions,
  updateCollectionPermission,
} from "../../permissions";
import type {
  CollectionIdProps,
  CollectionSidebarType,
} from "../../selectors/collection-permissions";
import {
  collectionsQuery,
  getCollectionEntity,
  getCollectionsPermissionEditor,
  getCollectionsSidebar,
  getIsDirty,
} from "../../selectors/collection-permissions";
import type { PermissionEditorEntity, PermissionEditorType } from "../../types";
import { assertNumericId } from "../../types";

const mapDispatchToProps = {
  initialize: initializeCollectionPermissions,
  loadPermissions: loadCollectionPermissions,
  navigateToItem: ({ id }: { id: CollectionId }) =>
    push(`/admin/permissions/collections/${id}`),
  updateCollectionPermission,
  savePermissions: saveCollectionPermissions,
};

const mapStateToProps = (state: State, props: CollectionIdProps) => {
  return {
    sidebar: getCollectionsSidebar(state, props),
    permissionEditor: getCollectionsPermissionEditor(state, props),
    isDirty: getIsDirty(state),
    collection: getCollectionEntity(state, props),
  };
};

type CollectionPermissionsPageProps = {
  params: CollectionIdProps["params"];
  sidebar: CollectionSidebarType;
  permissionEditor: PermissionEditorType | null;
  collection: Collection | undefined;
  navigateToItem: (item: any) => void;
  updateCollectionPermission: (params: UpdateCollectionPermissionParams) => any;
  isDirty: boolean;
  savePermissions: (...args: any[]) => any;
  loadPermissions: (...args: any[]) => any;
  initialize: (...args: any[]) => any;
  route: Route;
};

function CollectionsPermissionsPageView({
  sidebar,
  permissionEditor,
  collection,
  isDirty: collectionIsDirty,
  savePermissions,
  loadPermissions,
  updateCollectionPermission,
  navigateToItem,
  initialize,
  route,
}: CollectionPermissionsPageProps) {
  useListCollectionsTreeQuery(collectionsQuery);

  const originalPermissionsState = useSelector(
    ({ admin }) => admin.permissions.originalCollectionPermissions,
  );

  const dispatch = useDispatch();

  // MetrioniQ: dashboard-level permission overrides are edited on this same page,
  // so saving/loading has to cover both graphs and the dirty flag is the union.
  const dashboardPermissions = useSelector(
    ({ admin }) => admin.permissions.dashboardPermissions,
  );
  const originalDashboardPermissions = useSelector(
    ({ admin }) => admin.permissions.originalDashboardPermissions,
  );

  const dashboardIsDirty = useMemo(
    () =>
      JSON.stringify(dashboardPermissions ?? {}) !==
      JSON.stringify(originalDashboardPermissions ?? {}),
    [dashboardPermissions, originalDashboardPermissions],
  );

  const combinedIsDirty = collectionIsDirty || dashboardIsDirty;

  useEffect(() => {
    initialize();
    dispatch(initializeDashboardPermissions());
  }, [initialize, dispatch]);

  const handleSave = useCallback(async () => {
    if (collectionIsDirty) {
      await savePermissions();
    }
    if (dashboardIsDirty) {
      await dispatch(saveDashboardPermissions());
    }
  }, [collectionIsDirty, dashboardIsDirty, savePermissions, dispatch]);

  const handleLoad = useCallback(() => {
    loadPermissions();
    dispatch(loadDashboardPermissions());
  }, [loadPermissions, dispatch]);

  const handlePermissionChange = useCallback(
    (
      item: PermissionEditorEntity,
      _permission: unknown,
      value: unknown,
      toggleState: boolean | null,
    ) => {
      if (!collection) {
        return;
      }
      updateCollectionPermission({
        groupId: assertNumericId(item.id),
        collection,
        value,
        shouldPropagateToChildren: toggleState,
        originalPermissionsState,
      });
    },
    [collection, updateCollectionPermission, originalPermissionsState],
  );

  return (
    <PermissionsPageLayout
      tab="collections"
      isDirty={combinedIsDirty}
      route={route}
      onSave={handleSave}
      onLoad={handleLoad}
      helpContent={<CollectionPermissionsHelp />}
      key={collection?.id}
    >
      <PermissionsSidebar {...sidebar} onSelect={navigateToItem} />

      {!permissionEditor && (
        <PermissionsEditorEmptyState
          icon="folder"
          message={t`Select a collection to see its permissions`}
        />
      )}

      {permissionEditor && (
        <div style={{ flex: 1, overflow: "auto" }}>
          <PermissionsEditor
            isLoading={undefined}
            error={undefined}
            {...permissionEditor}
            onChange={handlePermissionChange}
          />

          {collection && (
            <DashboardPermissionsSection collectionId={collection.id} />
          )}
        </div>
      )}
    </PermissionsPageLayout>
  );
}

export const CollectionPermissionsPage = _.compose(
  connect(mapStateToProps, mapDispatchToProps),
)(CollectionsPermissionsPageView);
